import {
  PhAddressBookIcon,
  PhArrowsSplitIcon,
  PhForkKnifeIcon,
  PhHandCoinsIcon,
  PhMoneyIcon,
  PhPrinterIcon,
  PhSealPercentIcon,
  HeartIcon as PhHeartIcon,
} from "../icons";
import { TenderPicker } from "./TenderPicker";
import { useAuth } from "../../context/AuthContext";
import { BahsisPanel } from "./BahsisPanel";
import { MealCardPicker } from "./MealCardPicker";
import type { MealCard, PaymentMethod } from "../../types";

/** Düğmelerin altında açılan, ödemeyi tamamlayan panel. */
export type PayPanel = "none" | "tender" | "bahsis" | "meal";

/** Bir ödemeye eklenenler: üstü kalan para ya da yemek kartı markası. */
export interface PayExtras {
  bahsisKurus?: number;
  bahsisPaymentMethod?: "cash" | "card";
  mealCard?: MealCard;
}

interface Props {
  totalKurus: number;
  disabled: boolean;
  /** Açık panel; kasa klavye kısayollarıyla da açtığı için dışarıda tutuluyor. */
  panel: PayPanel;
  onPanel: (p: PayPanel) => void;
  /** Açık nakit/kart panelinin sonunda fiş basılsın mı. */
  tenderPrint: boolean;
  /** Öde & Kapat (false) ya da Öde, Yazdır & Kapat (true). */
  onTender: (print: boolean) => void;
  /** Yoksa İndirimli kapalı (ara ödemede indirim yok, 22 Eki 2026). */
  onDiscount?: () => void;
  /**
   * Ödemeyi tamamlar. print verilmediyse çağıranın kuralı karar veriyor
   * (kasada Gel-Al basılır, adisyon basılmaz).
   */
  onPay: (method: PaymentMethod, extras: PayExtras, print?: boolean) => void;
  /** Bahşiş modülü açıksa adı ("Bahşiş"); yoksa Üstü Kalsın görünmez. */
  bahsisLabel?: string;
  /** Cari modülü açıksa; hesabı seçme penceresini çağıran açıyor. */
  onDebtor?: () => void;
  /** Yalnız kasada, açık adisyonda. Parçalı Öde'nin içinde yok. */
  onSplit?: () => void;
  splitDisabled?: boolean;
  /** Yemek kartı modülü (kurum özelliği) açık mı. */
  mealOn?: boolean;
  /** Alt sıra (Üstü Kalsın, Parçalı Öde, Cari, Yemek Kartı) görünsün mü. */
  showExtras?: boolean;
  /** Müşteri sadakat modülü açıksa (30 Eki 2026): kart penceresini açar. */
  onLoyalty?: () => void;
  /** Satışa eklenmiş kart: "K7Q2M9 · 7/10". */
  loyaltyLabel?: string;
}

/**
 * Ödeme alanı (21 Eki 2026): kasanın sepetinde ve Parçalı Öde'nin üçüncü
 * sütununda aynısı (kullanıcı: "direkt onu oraya kopyala"). Her kişi kendi
 * payını kasadaki gibi istediği yöntemle ödüyor.
 */
export function PayActions({
  totalKurus,
  disabled,
  panel,
  onPanel,
  tenderPrint,
  onTender,
  onDiscount,
  onPay,
  bahsisLabel,
  onDebtor,
  onSplit,
  splitDisabled,
  mealOn,
  showExtras = true,
  onLoyalty,
  loyaltyLabel,
}: Props) {
  // Yazıcısız kurum (28 Eki 2026): yazdırlı düğme yok, Öde & Kapat ana düğme.
  const { usePrinter } = useAuth();
  return (
    <>
      <div className={`pay3 ${usePrinter ? "" : "pay3--two"}`}>
        {/* The labels sit in a span so a phone can drop them and keep the
            icons; title/aria-label carry the same words. */}
        <button
          type="button"
          className="pay3__btn pay3__btn--alt"
          onClick={onDiscount}
          disabled={disabled || !onDiscount}
          title="İndirimli"
          aria-label="İndirimli"
        >
          <PhSealPercentIcon />
          <span className="pay3__label">İndirimli</span>
        </button>
        <button
          type="button"
          className={`pay3__btn ${usePrinter ? "" : "pay3__btn--main"}`}
          onClick={() => onTender(false)}
          disabled={disabled}
          title="Öde & Kapat"
          aria-label="Öde ve Kapat"
        >
          <PhMoneyIcon />
          <span className="pay3__label">Öde &amp; Kapat</span>
        </button>
        {/* Rightmost and green: the one that ends most sales. */}
        {usePrinter && (
        <button
          type="button"
          className="pay3__btn pay3__btn--main"
          onClick={() => onTender(true)}
          disabled={disabled}
          title="Öde, Yazdır & Kapat"
          aria-label="Öde, Yazdır ve Kapat"
        >
          <PhPrinterIcon />
          <span className="pay3__label">Öde, Yazdır &amp; Kapat</span>
        </button>
        )}
      </div>

      {panel === "tender" && (
        <TenderPicker
          totalKurus={totalKurus}
          print={tenderPrint}
          onCancel={() => onPanel("none")}
          onPick={(method, print) => {
            onPanel("none");
            onPay(method, {}, print);
          }}
        />
      )}

      {panel === "bahsis" && bahsisLabel && (
        <BahsisPanel
          totalKurus={totalKurus}
          label={bahsisLabel}
          onCancel={() => onPanel("none")}
          onConfirm={(bahsisKurus, method) => {
            onPanel("none");
            // One answer covers both: the bill and the money on top are handed
            // over once, in one form. Print is left to the caller's rule.
            onPay(method, { bahsisKurus, bahsisPaymentMethod: method });
          }}
        />
      )}

      {panel === "meal" && (
        <MealCardPicker
          totalKurus={totalKurus}
          onCancel={() => onPanel("none")}
          onPick={(mealCard) => {
            onPanel("none");
            onPay("meal", { mealCard });
          }}
        />
      )}

      {/* One row for every extra ending, filled by whichever are available
          here. Hidden while a panel is open: two questions on screen at once
          is one too many at a counter. */}
      {panel === "none" && showExtras && (
        <div className="pay2">
          {onLoyalty && (
            <button
              type="button"
              className={`pay2__btn ${loyaltyLabel ? "pay2__btn--on" : ""}`}
              onClick={onLoyalty}
              disabled={disabled}
              title="Müşteri sadakat kartı"
            >
              <PhHeartIcon />
              <i className="pay__add-rule" />
              {loyaltyLabel ?? "Sadakat"}
            </button>
          )}
          {bahsisLabel && (
            <button
              type="button"
              className="pay2__btn"
              onClick={() => onPanel("bahsis")}
              disabled={disabled}
              title={`Üstü kalsın: farkı ${bahsisLabel.toLocaleLowerCase("tr")} olarak kaydet`}
            >
              <PhHandCoinsIcon />
              <i className="pay__add-rule" />
              Üstü Kalsın
            </button>
          )}

          {onSplit && (
            <button
              type="button"
              className="pay2__btn pay2__btn--split"
              onClick={onSplit}
              disabled={splitDisabled}
              title="Adisyonu parçalara bölüp ayrı ayrı öde"
            >
              <PhArrowsSplitIcon />
              <i className="pay__add-rule" />
              Parçalı Öde
            </button>
          )}

          {/* Putting a sale on an account is not a way of paying, the money
              has not arrived, so it sits apart from the endings above. */}
          {onDebtor && (
            <button
              type="button"
              className="pay2__btn pay2__btn--debt"
              onClick={onDebtor}
              disabled={disabled}
              title="Bu satışı bir kişinin hesabına yaz"
            >
              <PhAddressBookIcon />
              <i className="pay__add-rule" />
              Cari
            </button>
          )}

          {mealOn && (
            <button
              type="button"
              className="pay2__btn"
              onClick={() => onPanel("meal")}
              disabled={disabled}
              title="Yemek kartıyla öde (Ticket, Sodexo, Multinet…)"
            >
              <PhForkKnifeIcon />
              <i className="pay__add-rule" />
              Yemek Kartı
            </button>
          )}
        </div>
      )}
    </>
  );
}
